import { ft } from '../core/units.js';
import { EVENT } from './events.js';
import { PRANKS, PRANK_STOCK, byId } from '../data/pranks.js';

/**
 * Eight pranks, and the one rule each of them adds.
 *
 * What is *not* here is most of what a prank does. Nobody in this file decides
 * who saw anything, what a witness believes, whether it reaches your card, what
 * a person does about it or what it costs. All of that already exists and all
 * of it is asked the ordinary way: a prank puts something on the bus or on the
 * floor, and `perception.js`, `suspicion.js`, `witnesses.js` and the `reacts`
 * line on somebody's row answer it exactly as they answer a smashed bottle.
 *
 * So the file is small on purpose, and the size is the argument. Each prank is
 * one of these:
 *
 *   an event on the bus        the band, the popper, the yelp when ice goes
 *                              down a shirt
 *   a patch on the floor       spilled ice, the shrimp — a position, a radius
 *                              and what happens to somebody inside it
 *   an affliction on a person  the pepper, the note — a thing that is true
 *                              about them for a while
 *   a note in a diary          the salt, which is the same as the others with
 *                              the time it goes off written down
 *
 * **Unwitnessed is free, and it is free because nothing here writes a
 * consequence.** There is no path from a prank to your standing, your card or
 * anybody's opinion of you that does not go through a claim somebody formed by
 * seeing or hearing it. Two of the eight can cost you something — the salt's
 * complaint, which lands on the Kitchen unless a claim names you, and the
 * note's personal cost, which needs the person wearing it to hold a claim that
 * names you — and both of those ask perception the question rather than
 * answering it.
 */

/**
 * How sure somebody has to be before a claim counts as naming you.
 *
 * Used by exactly two pranks, and it is the same number for both. A glance
 * across a ballroom is a claim and it is not an accusation; this is where the
 * line between them is for a thing nobody is going to file about.
 */
export const NAMES_YOU = 0.45;

/** Seconds between a patch deciding it has caught somebody and the next one. */
const SLIP_COOLDOWN = 6;

export function createPranks({
  bus,
  entities,
  throwing,
  perception,
  npcs,
  clock,
  regard = null,
  standing = null,
  hud = null,
  onComplaint = null,
}) {
  /** What is on the floor: spilled ice and forgotten shrimp. */
  let patches = [];
  /** What is true about a person for a while: a nose full of pepper, a note. */
  let afflictions = [];
  /** And what is waiting to go off: the sugar caddy. */
  let armed = [];
  /** Who has slipped recently, so one patch is not a treadmill. */
  const slipped = new Map();
  let seconds = 0;
  let serial = 0;

  const minutes = () => clock.day * 24 * 60 + clock.minutes;
  const npcById = (id) => npcs.find((npc) => npc.data.id === id) ?? null;

  /**
   * The popper, pulling people toward where it went off.
   *
   * On the impact rather than on the throw, because what draws somebody is
   * where the bang was and not where it was let go of — which is the entire
   * point of a distraction and the reason it is worth lobbing one across a
   * room. `goAndLook` is the existing behaviour: it is what a suspicious
   * person does when they lose sight of you, and nothing in `npc.js` learns
   * that a popper exists.
   */
  const stopListening = bus.on(EVENT.IMPACT, (event) => {
    if (event.kind !== 'popper') return;
    const prank = byId.popper;
    for (const npc of npcs) {
      if (!npc.present || npc.aboard) continue;
      const gap = Math.hypot(
        npc.position.x - event.position.x,
        npc.position.z - event.position.z
      );
      if (gap > ft(prank.draws)) continue;
      npc.goAndLook({ x: event.position.x, z: event.position.z });
    }
  });

  /**
   * How sure the surest claim about the player is, of a given kind.
   *
   * The only question this file ever asks about being seen, and it asks it of
   * perception. `by` narrows it to one person, which is what the note needs:
   * the whole cost of a kick-me note is whether the person wearing it worked
   * out who put it there, and that is a claim *they* hold.
   */
  function claimAbout({ what = null, by = null, since = null } = {}) {
    let best = 0;
    for (const record of perception.witnesses) {
      if (record.subject) continue;
      if (by && record.by !== by) continue;
      if (what && record.what !== what) continue;
      if (since !== null && record.opened < since) continue;
      best = Math.max(best, record.confidence);
    }
    return best;
  }

  /**
   * A thing that is true about a person for a while.
   *
   * One shape for both the pepper and the note, because they are the same
   * shape: somebody has a thing, it wears off, and while it is on, something
   * reads it. What reads them is not in here — `sights()` hands the note to
   * perception and `sightOf()` hands the pepper to whoever draws a cone.
   */
  function afflict(kind, npcId, { until, ...rest }) {
    afflictions = afflictions.filter(
      (entry) => !(entry.kind === kind && entry.npc === npcId)
    );
    afflictions.push({ kind, npc: npcId, until, ...rest });
  }

  function afflictionOf(kind, npcId) {
    return afflictions.find((entry) => entry.kind === kind && entry.npc === npcId) ?? null;
  }

  return {
    get patches() {
      return patches.map((patch) => ({ ...patch }));
    },
    get afflictions() {
      return afflictions.map((entry) => ({ ...entry }));
    },
    get armed() {
      return armed.map((entry) => ({ ...entry }));
    },

    /** Where each prank is stocked, for whoever builds the prompts. */
    get stock() {
      return PRANK_STOCK;
    },

    // --- the shapes -----------------------------------------------------------

    /**
     * Something aimed at a person you are standing behind.
     *
     * Ice and the note both land here, and so does the pepper, which is handed
     * over rather than dropped down a collar. What they have in common is the
     * only rule this function has: **the act is offered to the world as an
     * event, and who saw it is perception's answer.** Whether the *target*
     * could see you is not checked here either — the event goes on the bus and
     * perception decides, which means the target is just another pair of eyes
     * and the rule holds for them for the same reason it holds for everybody.
     */
    doTo(id, npcId, { from }) {
      const prank = byId[id];
      const who = npcById(npcId);
      if (!prank || prank.shape !== 'done' || !who || !who.present) {
        return { ok: false, reason: 'nobody there' };
      }
      const theirs = { x: who.position.x, y: 0, z: who.position.z };
      const now = minutes();

      /*
       * The act happens where *you* are, not where they are.
       *
       * Which sounds like a detail and is the whole of "unseen by the target is
       * free". An act emitted at the target's own feet is an act the target is
       * standing zero feet from, and perception quite correctly hands anybody a
       * claim about a thing happening at arm's length whatever way they are
       * facing — so ice down a shirt was witnessed by the person wearing it
       * every single time, and the one prank whose entire point is doing it
       * from behind could not be done from behind.
       *
       * So the claim is formed about a thing that happened where the player was
       * standing, and the target is one more pair of eyes deciding whether they
       * could see it. Their yelp is emitted separately, at them, because that
       * is where the noise is.
       */
      bus.emit(EVENT.PRANK, { actor: 'player', prank: id, target: npcId, position: { ...from }, at: theirs });
      if (prank.noise) {
        bus.emit(EVENT.NOISE, {
          actor: npcId,
          cause: 'prank',
          kind: id,
          position: theirs,
          radius: ft(prank.noise),
        });
      }

      if (id === 'ice') {
        // They jump, they turn round, and they look at whatever is behind them.
        who.hold(prank.flinch, { at: { x: from.x, z: from.z } });
      }
      if (id === 'pepper') {
        afflict('pepper', npcId, {
          until: seconds + prank.lasts,
          sight: prank.sight,
          every: prank.every,
          nextSneeze: seconds + prank.every,
        });
      }
      if (id === 'note') {
        afflict('note', npcId, { until: null, untilMinutes: now + prank.lasts, opened: now });
      }
      return { ok: true, prank: id, target: npcId };
    },

    /**
     * Something you throw. It is a throw, and that is the whole of it.
     *
     * `throwing.launch` does the flying, the landing, the impact and the noise,
     * and the catalogue row decides how loud and how hard. A rubber band and a
     * party popper reach this function identically and come out of it as
     * completely different things, which is the argument for having a
     * catalogue at all.
     */
    fling(id, { from, yaw, pitch = 0, power = 1 }) {
      const prank = byId[id];
      if (!prank || prank.shape !== 'thrown') return { ok: false, reason: 'not a thing you throw' };
      throwing.launch({ kind: prank.kind, from, yaw, pitch, power, actor: 'player' });
      return { ok: true, prank: id, endless: Boolean(prank.endless) };
    },

    /**
     * Something you leave on a floor.
     *
     * A position, a radius and an expiry. What it does to whoever walks into it
     * is decided in `update` and is one line each.
     */
    leave(id, at) {
      const prank = byId[id];
      if (!prank || prank.shape !== 'left') return { ok: false, reason: 'not a thing you put down' };
      serial += 1;
      const patch = {
        id: `${id}-${serial}`,
        prank: id,
        position: { x: at.x, y: 0, z: at.z },
        laid: minutes(),
        gone: minutes() + prank.melts,
      };
      patches = [...patches, patch];
      bus.emit(EVENT.PRANK, { actor: 'player', prank: id, position: patch.position });
      return { ok: true, prank: id, patch: patch.id };
    },

    /**
     * Something you do to a thing, that happens later.
     *
     * The claim is taken *now*, at the moment your hands are in the caddy, and
     * carried in the note rather than asked for when it goes off. Which is the
     * only honest way round: forty-five minutes later nobody is looking at a
     * sugar caddy, so asking then would mean every salting in the game lands on
     * the Kitchen and the prank would have no risk in it at all.
     */
    doctor(id, at) {
      const prank = byId[id];
      if (!prank || prank.shape !== 'doctored') return { ok: false, reason: 'not that kind' };
      const now = minutes();
      serial += 1;
      bus.emit(EVENT.PRANK, { actor: 'player', prank: id, position: { ...at } });
      armed.push({
        id: `${id}-${serial}`,
        prank: id,
        position: { x: at.x, y: 0, z: at.z },
        at: now + prank.delay,
        /*
         * Who saw your hands in it, taken on the frame it happened. A claim
         * formed from this act, not from anything else you have done today —
         * which is why it is filtered on `prank` and on the minute.
         */
        seen: Math.max(claimAbout({ what: 'prank', since: now - 1 }), 0),
      });
      return { ok: true, prank: id };
    },

    // --- what a prank reads ---------------------------------------------------

    /**
     * What is left of somebody's sight, 0..1.
     *
     * Read by whoever owns the vision cone. One multiplier, no special case for
     * a prank: a person with a nose full of pepper has a smaller cone in exactly
     * the way a person in a dark room would.
     */
    sightOf(npcId) {
      const pepper = afflictionOf('pepper', npcId);
      return pepper ? pepper.sight : 1;
    },

    /**
     * The things in the world that are not the player and can be looked at.
     *
     * Handed straight to `perception.observe`, which already has this path —
     * it is how a javelina standing by the dock doors becomes something people
     * form opinions about. A note on somebody's back is the same kind of fact.
     */
    sights() {
      return afflictions
        .filter((entry) => entry.kind === 'note')
        .map((entry) => {
          const who = npcById(entry.npc);
          if (!who || !who.present) return null;
          return {
            key: `note:${entry.npc}`,
            what: 'note',
            detail: 'a note on their back',
            subject: entry.npc,
            position: { x: who.position.x, y: 0, z: who.position.z },
          };
        })
        .filter(Boolean);
    },

    /** Whether this person is wearing one, for whatever draws them. */
    noteOn(npcId) {
      return Boolean(afflictionOf('note', npcId));
    },

    /**
     * Whether a point is inside a smell, and how strong it is there.
     *
     * Zero everywhere else, which is what makes this cheap to ask every frame
     * for everybody.
     */
    smellAt(point) {
      let worst = 0;
      for (const patch of patches) {
        if (patch.prank !== 'shrimp') continue;
        const prank = byId.shrimp;
        const age = Math.min(1, (minutes() - patch.laid) / prank.ripens);
        const reach = ft(prank.radius + (prank.grows - prank.radius) * age);
        const gap = Math.hypot(point.x - patch.position.x, point.z - patch.position.z);
        if (gap > reach) continue;
        worst = Math.max(worst, 1 - gap / reach);
      }
      return worst;
    },

    // --- the frame ------------------------------------------------------------

    /**
     * Everything that is standing, once a frame.
     *
     * Four small loops and each one is a prank's single rule. `bodies` is
     * whoever can slip — the player and everybody on shift — offered the same
     * way, because a server going over on spilled ice and the player going over
     * on it are the same event and it would be absurd to write it twice.
     */
    update(dt, { bodies = [] } = {}) {
      seconds += dt;
      const now = minutes();

      // 1. Ice on a floor: anybody through it goes down and drops a unit.
      const slippery = patches.filter((patch) => patch.prank === 'ice-spill');
      const slips = [];
      if (slippery.length) {
        for (const body of bodies) {
          const last = slipped.get(body.id) ?? -Infinity;
          if (seconds - last < SLIP_COOLDOWN) continue;
          for (const patch of slippery) {
            const gap = Math.hypot(
              body.position.x - patch.position.x,
              body.position.z - patch.position.z
            );
            if (gap > ft(byId['ice-spill'].radius)) continue;
            slipped.set(body.id, seconds);
            slips.push({ id: body.id, at: { ...patch.position } });
            /*
             * The noise of somebody going over, which is the only thing about
             * a slip anybody else perceives. Whether that reaches the player
             * is the usual question, asked by the usual module — the patch
             * carries no memory of who laid it.
             */
            bus.emit(EVENT.NOISE, {
              actor: body.id,
              cause: 'slip',
              kind: 'ice-spill',
              position: { x: body.position.x, y: 0, z: body.position.z },
              radius: ft(30),
            });
            break;
          }
        }
      }

      // 2. A smell that grows: anybody inside it goes somewhere else.
      const left = [];
      for (const patch of patches) {
        if (patch.prank !== 'shrimp') continue;
        const prank = byId.shrimp;
        const age = Math.min(1, (now - patch.laid) / prank.ripens);
        const reach = ft(prank.radius + (prank.grows - prank.radius) * age);
        for (const npc of npcs) {
          if (!npc.present || npc.searching || npc.holding) continue;
          const gap = Math.hypot(
            npc.position.x - patch.position.x,
            npc.position.z - patch.position.z
          );
          if (gap > reach) continue;
          /*
           * Away from it, which `hold` already knows how to mean. Nothing here
           * says a guest complains about it: their row says they complain about
           * things, and being driven out of a room by a smell is a thing.
           */
          npc.hold(4, { at: { x: patch.position.x, z: patch.position.z }, away: true });
          left.push({ id: npc.data.id, patch: patch.id });
        }
      }

      // 3. Pepper: a sneeze every few seconds, loud, and it costs them a unit.
      const sneezes = [];
      for (const entry of afflictions) {
        if (entry.kind !== 'pepper') continue;
        if (seconds < entry.nextSneeze) continue;
        entry.nextSneeze = seconds + entry.every;
        const who = npcById(entry.npc);
        if (!who || !who.present) continue;
        sneezes.push({ id: entry.npc, at: { x: who.position.x, z: who.position.z } });
        bus.emit(EVENT.NOISE, {
          actor: entry.npc,
          cause: 'sneeze',
          kind: 'pepper',
          position: { x: who.position.x, y: 0, z: who.position.z },
          radius: ft(byId.pepper.noise),
        });
      }

      // 4. The caddy, going off.
      const fired = [];
      const stillArmed = [];
      for (const entry of armed) {
        if (now < entry.at) {
          stillArmed.push(entry);
          continue;
        }
        const prank = byId[entry.prank];
        /*
         * Whose it is. The claim taken at the moment your hands were in it is
         * the whole question: over the line and it is yours, under it and the
         * complaint goes where a complaint about a sugar caddy goes, which is
         * the Kitchen.
         */
        const mine = entry.seen >= NAMES_YOU;
        /*
         * Who wears it, and what that can mean here.
         *
         * A complaint about a coffee service lands on the Kitchen, and the
         * Kitchen wearing it is *not something the player's ledger can say*:
         * every number in `standing` is what a department thinks of the player,
         * so charging Kitchen for it would be charging the player for a thing
         * nobody saw them do. So when nobody saw, nothing moves at all. It
         * happened, somebody is annoyed about it, and it is not yours.
         *
         * When somebody did see, it is yours, and it goes where every
         * complaint in this game goes: Management, who were told.
         */
        const department = mine ? 'management' : 'kitchen';
        if (mine && standing) {
          standing.apply('management', prank.cost, {
            reason: 'a complaint about the coffee, and somebody saw whose hands were in it',
          });
        }
        onComplaint?.({ prank: entry.prank, department, mine, seen: entry.seen });
        if (hud) {
          hud.say(
            mine
              ? 'Somebody has told a manager whose hands were in that caddy.'
              : 'A complaint about the coffee service. The Kitchen is wearing it.',
            { tone: 'bad', seconds: 5 }
          );
        }
        fired.push({ prank: entry.prank, department, mine, seen: entry.seen });
      }
      armed = stillArmed;

      // 5. And what a note costs you, which is only ever personal.
      const told = [];
      for (const entry of afflictions) {
        if (entry.kind !== 'note' || entry.paid) continue;
        /*
         * They have to work out it was you. A claim *they* hold, about the act
         * itself, sure enough to be an accusation rather than a glimpse — and
         * if nobody ever tells them, it never costs anything, which is the
         * right shape for a joke.
         */
        const sure = claimAbout({ what: 'prank', by: entry.npc, since: entry.opened - 1 });
        if (sure < NAMES_YOU) continue;
        entry.paid = true;
        if (regard && regard.ids.includes(entry.npc)) {
          regard.applyDirect({ [entry.npc]: byId.note.regard }, { reason: 'the note on their back' });
        }
        told.push({ id: entry.npc, sure });
      }

      patches = patches.filter((patch) => patch.gone > now);
      afflictions = afflictions.filter((entry) =>
        entry.kind === 'note' ? entry.untilMinutes > now : entry.until > seconds
      );

      return { slips, left, sneezes, fired, told };
    },

    /** Lets go of the bus. Nothing in the game calls it; the tests do. */
    close() {
      stopListening();
    },

    clear() {
      patches = [];
      afflictions = [];
      armed = [];
      slipped.clear();
      seconds = 0;
      serial = 0;
    },

    toJSON: () => ({
      patches: patches.map((patch) => ({ ...patch })),
      afflictions: afflictions.map((entry) => ({ ...entry })),
      armed: armed.map((entry) => ({ ...entry })),
    }),

    load(state) {
      patches = [...(state?.patches ?? [])];
      afflictions = [...(state?.afflictions ?? [])];
      armed = [...(state?.armed ?? [])];
      slipped.clear();
      /*
       * The seconds clock starts again, because it is a clock about this
       * session's frames rather than about the hotel. Anything measured on it —
       * a nose full of pepper, a slip cooldown — is seconds long, and a save
       * that came back with somebody mid-sneeze would be stranger than one that
       * did not.
       */
      seconds = 0;
      afflictions = afflictions.filter((entry) => entry.kind === 'note');
    },
  };
}

export { PRANKS, byId };
